import { useEffect } from 'react';
import { toast } from 'sonner';
import { api } from '@/services/api';

const TRACE_RESULT_EVENTS = new Set([
  'TELCOOS_TRACE_RESULT',
  'BROWSER_TRACE_RESULT',
]);

function getHarFromTraceMessage(data: unknown): unknown {
  if (!data || typeof data !== 'object') return null;

  const record = data as Record<string, unknown>;
  if (record.har) return record.har;
  if (record.log) return record;

  const result = record.result ?? record.response;
  if (result && typeof result === 'object') {
    const resultRecord = result as Record<string, unknown>;
    if (resultRecord.har) return resultRecord.har;
    if (resultRecord.log) return resultRecord;

    const trace = resultRecord.trace;
    if (trace && typeof trace === 'object') {
      const traceRecord = trace as Record<string, unknown>;
      return traceRecord.har ?? trace;
    }
  }

  const trace = record.trace;
  if (trace && typeof trace === 'object') {
    const traceRecord = trace as Record<string, unknown>;
    return traceRecord.har ?? trace;
  }

  return null;
}

export function BrowserTraceBridge() {
  useEffect(() => {
    const onMessage = async (event: MessageEvent) => {
      if (event.source !== window) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;

      const type = (data as { type?: unknown }).type;
      if (typeof type !== 'string' || !TRACE_RESULT_EVENTS.has(type)) return;

      const har = getHarFromTraceMessage(data);
      if (!har) {
        toast.error('Trace did not include a HAR payload.');
        return;
      }

      try {
        const saved = await api.browserTraces.upload(har);
        window.dispatchEvent(new CustomEvent('telcoos-browser-trace-saved', { detail: saved }));
        window.postMessage({ type: 'TELCOOS_TRACE_SAVED', traceId: saved.traceId, path: saved.path }, '*');
        toast.success(`Browser trace saved: ${saved.path}`);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to save browser trace.';
        toast.error(message);
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return null;
}

export default BrowserTraceBridge;
